import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bell, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";
import type { ModuleKey } from "@/components/common/MiniMirrorLiveButton";

interface LiveComingSoonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  module: ModuleKey;
}

export function LiveComingSoonModal({ open, onOpenChange, module }: LiveComingSoonModalProps) {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);

  const handleNotifyMe = async () => {
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSubmitting(false);
        return;
      }
      const { error } = await supabase
        .from("live_waitlist")
        .insert({ user_id: user.id, module });
      // Duplicate violation (UNIQUE user_id, module) — user already opted in.
      // Treat as soft success so re-tapping the button is idempotent.
      if (error && (error as { code?: string }).code !== "23505") {
        console.error("[LiveComingSoonModal] insert failed", error);
        setSubmitting(false);
        return;
      }
      toast.success(t("common.liveComingSoon.added"));
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{t("common.liveComingSoon.title")}</DialogTitle>
          <DialogDescription>{t("common.liveComingSoon.body")}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-2">
          <Button
            type="button"
            onClick={handleNotifyMe}
            disabled={submitting}
            className="w-full"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Bell className="w-4 h-4 mr-2" />
                {t("common.liveComingSoon.notifyMe")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
